import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import type { EntityOrganizationUser } from '@interfaces/user';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { OrganizationUserSelect } from '@ovComponents/3-pattern/organizationUserSelect/organizationUserSelect';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { Box, CircularProgress, Grid, Typography } from 'hephaestus/web';
import { uniq } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { FormSignerMapping } from './formSignerMapping';

type ManageSignersSubStepOptions = {
  subtitle?: TranslatedString;
  editSigningOrder?: boolean;
  autoAssignCurrentUser?: boolean;
};

type SignerUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
  id?: string;
};

const FETCH_SIGNER_TYPES = gql`
  query fetchSignerTypes($input: FetchSignerTypesInput!) {
    fetchSignerTypes(input: $input) {
      signerTypes {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        isPrimary
        defaultSigningOrder
      }
      totalCount
    }
  }
`;

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        type
        accessibleBy(input: { excludeRepCodeUsers: true }) {
          relation
          organizationUser {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        state
        signingType
        requiredSignatures {
          signerKey
        }
      }
      totalCount
    }
  }
`;

export const ManageSigners = ({
  onNext,
  workflowCompletion,
  options,
}: {
  onNext: () => void;
  workflowCompletion: any;
  options: ManageSignersSubStepOptions;
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const { activeOrganization, userContext } = useContext(UserContext);
  const [signerMapping, setSignerMapping] = useState<any[]>([]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [signerMappingLoading, setSignerMappingLoading] = useState(true);

  const { data: formCompletionsData } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !workflowCompletion?.id,
  });

  const { data } = useQuery(FETCH_SIGNER_TYPES, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: { perPage: 1000 },
      },
    },
  });

  const { data: userData } = useQuery(FETCH_USER, {
    fetchPolicy: 'no-cache',
    variables: {
      userId: workflowCompletion.objectId,
    },
  });

  useEffect(() => {
    if (
      formCompletionsData?.fetchFormCompletions?.formCompletions &&
      data?.fetchSignerTypes?.signerTypes &&
      userData?.fetchUser?.user
    ) {
      let signerKeys: string[] = [];
      formCompletionsData.fetchFormCompletions.formCompletions.forEach(
        (formCompletion: any) => {
          signerKeys.push(
            ...formCompletion.requiredSignatures.map(
              (signature: any) => signature.signerKey,
            ),
          );
        },
      );
      signerKeys = uniq(signerKeys);
      const currentSignerMapping =
        workflowCompletion.context.signerMapping || [];

      // If auto-assigning the current user is enabled and there is only one signer key, assign the current user as the default signer and move to the next step
      const autoAssignCurrentUser =
        options.autoAssignCurrentUser && signerKeys.length === 1;

      let defaultSignerUser: SignerUser | undefined;
      if (autoAssignCurrentUser) {
        const signerUser =
          userData.fetchUser.user.type !== 'INDIVIDUAL'
            ? userData.fetchUser.user.accessibleBy.find(
                (item: EntityOrganizationUser) => !!item.organizationUser,
              )?.organizationUser
            : undefined;

        defaultSignerUser = {
          id: signerUser ? signerUser.id : userContext.id,
          firstName: signerUser ? signerUser.firstName : userContext.firstName,
          lastName: signerUser ? signerUser.lastName : userContext.lastName,
          email: signerUser ? signerUser.email : userContext.email,
        };
      }
      const formSigners = signerKeys
        .map((signerKey: string) => {
          let signer = currentSignerMapping.find(
            (s: any) => s.signerKey === signerKey,
          );
          const signerType = data.fetchSignerTypes.signerTypes.find(
            (type: any) => type.key === signerKey,
          );
          if (!signerType) {
            return undefined;
          }
          if (!signer && defaultSignerUser) {
            signer = {
              signerName: signerType.translatedName,
              firstName: defaultSignerUser.firstName,
              lastName: defaultSignerUser.lastName,
              email: defaultSignerUser.email,
              organizationUserId: defaultSignerUser.id,
            };
          }
          return {
            signerKey,
            signerName: signerType.translatedName,
            firstName: signer?.firstName,
            lastName: signer?.lastName,
            email: signer?.email,
            organizationUserId: signer?.organizationUserId,
            useOrgUser: true,
            signingOrder: signerType?.defaultSigningOrder,
          };
        })
        .filter((s) => s !== undefined) as FormSignerMapping[];
      setSignerMapping(formSigners);

      if (autoAssignCurrentUser && formSigners.length === 1) {
        save(formSigners);
      } else {
        setSignerMappingLoading(false);
      }
    }
  }, [workflowCompletion, data, userData, formCompletionsData]);

  const save = async (formSigners?: FormSignerMapping[]) => {
    await setWorkflowData({
      ...workflowData,
      signerMapping: formSigners ?? signerMapping,
    });
    await onNext();
  };

  if (signerMappingLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          minHeight: '300px',
        }}
      >
        <Typography variant='headingMedium'>
          {t('manageSigners.manageSigners')}
        </Typography>
        <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
          {t('manageSigners.description')}
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={() => save()}
      submitButtonProps={{
        label: t('continue'),
        dataTestId: 'manage-signers-continue-button',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 2 }}>
          {t('manageSigners.manageSigners')}
        </Typography>
        {options?.subtitle && (
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Typography>
        )}
        {signerMapping.map((signer: FormSignerMapping) => (
          <Box
            key={signer.signerKey}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <Card variant='outlined' sx={{ width: '100%' }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant='bodyLarge'>
                    {translateBackend(signer.signerName)}
                  </Typography>
                  <SwitchField
                    checked={signer.useOrgUser}
                    label={t('manageSigners.useOrgUser')}
                    onChange={(e: any) =>
                      setSignerMapping(
                        signerMapping.map((s: FormSignerMapping) =>
                          s.signerKey === signer.signerKey
                            ? { ...s, useOrgUser: e }
                            : s,
                        ),
                      )
                    }
                  />
                </Box>
                {signer.useOrgUser ? (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid
                      size={{ xs: 12, sm: options.editSigningOrder ? 9 : 12 }}
                    >
                      <OrganizationUserSelect
                        label={t('manageSigners.user')}
                        value={signer.organizationUserId ?? ''}
                        onChange={(value: any) =>
                          setSignerMapping(
                            signerMapping.map((s: FormSignerMapping) =>
                              s.signerKey === signer.signerKey
                                ? {
                                    ...s,
                                    organizationUserId: value?.id,
                                    firstName: value?.firstName,
                                    lastName: value?.lastName,
                                    email: value?.email,
                                  }
                                : s,
                            ),
                          )
                        }
                      />
                    </Grid>
                    {options.editSigningOrder && (
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <NumberField
                          sx={{ width: '100px' }}
                          label={t('manageSigners.signingOrder')}
                          number={signer.signingOrder?.toString() ?? ''}
                          setNumber={(value: string) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? { ...s, signingOrder: parseInt(value, 10) }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t('manageSigners.firstName')}
                        value={signer.firstName}
                        onChange={(e: any) =>
                          setSignerMapping(
                            signerMapping.map((s: FormSignerMapping) =>
                              s.signerKey === signer.signerKey
                                ? { ...s, firstName: e.target.value }
                                : s,
                            ),
                          )
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t('manageSigners.lastName')}
                        value={signer.lastName}
                        onChange={(e: any) =>
                          setSignerMapping(
                            signerMapping.map((s: FormSignerMapping) =>
                              s.signerKey === signer.signerKey
                                ? { ...s, lastName: e.target.value }
                                : s,
                            ),
                          )
                        }
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: options.editSigningOrder ? 9 : 12 }}
                    >
                      <TextField
                        fullWidth
                        label={t('manageSigners.email')}
                        value={signer.email}
                        onChange={(e: any) =>
                          setSignerMapping(
                            signerMapping.map((s: FormSignerMapping) =>
                              s.signerKey === signer.signerKey
                                ? { ...s, email: e.target.value }
                                : s,
                            ),
                          )
                        }
                      />
                    </Grid>
                    {options.editSigningOrder && (
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <NumberField
                          label={t('manageSigners.signingOrder')}
                          number={signer.signingOrder?.toString() ?? ''}
                          setNumber={(value: string) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? { ...s, signingOrder: parseInt(value, 10) }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </WorkflowCompletionForm>
  );
};
